import { Model, Factory, Response } from 'miragejs'
import type { ModelInstance } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randCompanyName, randNumber, randBoolean } from '@ngneat/falso'

import type { SchemaAttributes } from '../../../types'
import {
  sortable,
  pageable,
  filterable,
  createErrorResponse,
} from '../../utils.js'
import type { MirageServer, MockServer } from '../../server'

type LaboratoryGroupData = SchemaAttributes<'LaboratoryGroup'>

export const model = Model as ModelDefinition<LaboratoryGroupData>

export const factory = Factory.extend({
  name() {
    return `${randCompanyName()} Lab Group`
  },
  never_require_results() {
    return randBoolean()
  },
  split_referral() {
    return randBoolean()
  },
  send_to_central_laboratory() {
    return randBoolean()
  },
  is_microbiology() {
    return randBoolean()
  },
  analyses_count() {
    return randNumber({ min: 0, max: 50 })
  },
})

function serialize(group: ModelInstance<LaboratoryGroupData>) {
  return {
    id: group.id,
    type: 'LaboratoryGroup',
    attributes: {
      name: group.attrs.name,
      never_require_results: group.attrs.never_require_results,
      split_referral: group.attrs.split_referral,
      send_to_central_laboratory: group.attrs.send_to_central_laboratory,
      is_microbiology: group.attrs.is_microbiology,
      analyses_count: group.attrs.analyses_count,
    },
  }
}

export function routes(server: MockServer) {
  server.get(
    '/catalog/management/laboratory-groups/v1',
    function (this: MirageServer, schema, request) {
      const allGroups = schema.all('laboratoryGroup')

      const sorted = sortable(allGroups, request, {
        name: (item) => item.attrs.name,
      })

      const filtered = filterable(sorted, request, {
        name: (item, value) =>
          item.attrs.name.toLowerCase().includes(value.toLowerCase()),
      })

      const { data, meta } = pageable(filtered, request)

      return {
        data: (data.models as ModelInstance<LaboratoryGroupData>[]).map(
          serialize,
        ),
        meta: { pagination: meta },
      }
    },
  )

  server.get(
    '/catalog/management/laboratory-groups/v1/:id',
    (schema, request) => {
      const id = request.params.id
      const group = schema.find(
        'laboratoryGroup',
        id,
      ) as ModelInstance<LaboratoryGroupData> | null

      if (!group) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `LaboratoryGroup with id ${id} not found` }],
        })
      }

      return { data: serialize(group) }
    },
  )

  server.post('/catalog/management/laboratory-groups/v1', (schema, request) => {
    const attrs = JSON.parse(request.requestBody)

    if (!attrs?.data?.attributes?.name) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "name" in request body' }],
      })
    }

    const { id, attributes } = attrs.data
    const createdRecord = schema.create('laboratoryGroup', {
      id: id || undefined,
      name: attributes.name,
      never_require_results: attributes.never_require_results ?? false,
      split_referral: attributes.split_referral ?? false,
      send_to_central_laboratory:
        attributes.send_to_central_laboratory ?? false,
      is_microbiology: attributes.is_microbiology ?? false,
      analyses_count: 0,
    }) as ModelInstance<LaboratoryGroupData>

    return {
      data: serialize(createdRecord),
    }
  })

  server.patch(
    '/catalog/management/laboratory-groups/v1/:id',
    (schema, request) => {
      const id = request.params.id
      const attrs = JSON.parse(request.requestBody)
      const group = schema.find(
        'laboratoryGroup',
        id,
      ) as ModelInstance<LaboratoryGroupData> | null

      if (!group) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `LaboratoryGroup with id ${id} not found` }],
        })
      }

      if (!attrs?.data?.attributes) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Missing "attributes" in request body' }],
        })
      }

      const {
        name,
        never_require_results,
        split_referral,
        send_to_central_laboratory,
        is_microbiology,
      } = attrs.data.attributes

      group.update({
        name: name ?? group.attrs.name,
        never_require_results:
          never_require_results ?? group.attrs.never_require_results,
        split_referral: split_referral ?? group.attrs.split_referral,
        send_to_central_laboratory:
          send_to_central_laboratory ?? group.attrs.send_to_central_laboratory,
        is_microbiology: is_microbiology ?? group.attrs.is_microbiology,
      })

      return { data: serialize(group) }
    },
  )

  server.delete(
    '/catalog/management/laboratory-groups/v1/:id',
    (schema, request) => {
      const id = request.params.id
      const group = schema.find('laboratoryGroup', id)

      if (!group) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `LaboratoryGroup with id ${id} not found` }],
        })
      }

      group.destroy()

      return new Response(204, {}, '')
    },
  )
}

export function seeds(server: MockServer) {
  server.create('laboratoryGroup', {
    name: 'Haematology',
    never_require_results: false,
    split_referral: false,
    send_to_central_laboratory: false,
    is_microbiology: false,
    analyses_count: 12,
  })
  server.create('laboratoryGroup', {
    name: 'Biochemistry',
    never_require_results: false,
    split_referral: true,
    send_to_central_laboratory: true,
    is_microbiology: false,
    analyses_count: 25,
  })
  server.create('laboratoryGroup', {
    name: 'Urinalysis',
    never_require_results: true,
    split_referral: false,
    send_to_central_laboratory: false,
    is_microbiology: false,
    analyses_count: 8,
  })
  server.create('laboratoryGroup', {
    name: 'Microbiology',
    never_require_results: false,
    split_referral: false,
    send_to_central_laboratory: true,
    is_microbiology: true,
    analyses_count: 15,
  })
  server.create('laboratoryGroup', {
    name: 'Cytology',
    never_require_results: false,
    split_referral: true,
    send_to_central_laboratory: false,
    is_microbiology: false,
    analyses_count: 6,
  })

  server.createList('laboratoryGroup', 5)
}
